import { useState, useRef, useEffect } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, CartesianGrid, XAxis, YAxis, LabelList, LineChart, Line } from 'recharts';
import { ChevronDown, Sun, Wind, Home, Calendar } from 'lucide-react';
// Import the CSV data functions
import { getParsedCsvData, getDataForDay, getAvailableDays } from '../../data/findhorn_data.js';

// Timeline Dropdown Component (updated for Day 1-7 selection)
const TimelineDropdown = ({ selectedPeriod, onPeriodChange, label = "Timeline" }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState('day-1');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Generate period options for the past 2 years + CSV days
    const generatePeriodOptions = () => {
        const options = [];
        const today = new Date();

        // Single day options
        options.push({
            value: 'today',
            label: 'Today',
            startDate: today,
            endDate: today,
            type: 'day'
        });

        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        options.push({
            value: 'yesterday',
            label: 'Yesterday',
            startDate: yesterday,
            endDate: yesterday,
            type: 'day'
        });

        // Week periods
        options.push({ value: 'separator1', label: '--- Weeks ---', disabled: true });

        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay());
        options.push({
            value: 'this-week',
            label: 'This Week',
            startDate: thisWeekStart,
            endDate: today,
            type: 'week'
        });

        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(thisWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(thisWeekStart);
        lastWeekEnd.setDate(thisWeekStart.getDate() - 1);
        options.push({
            value: 'last-week',
            label: 'Last Week',
            startDate: lastWeekStart,
            endDate: lastWeekEnd,
            type: 'week'
        });

        // Month periods
        options.push({ value: 'separator2', label: '--- Months ---', disabled: true });

        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        options.push({
            value: 'this-month',
            label: 'This Month',
            startDate: thisMonthStart,
            endDate: today,
            type: 'month'
        });

        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        options.push({
            value: 'last-month',
            label: 'Last Month',
            startDate: lastMonthStart,
            endDate: lastMonthEnd,
            type: 'month'
        });

        // Custom period
        options.push({ value: 'separator3', label: '--- Custom ---', disabled: true });
        options.push({
            value: 'custom',
            label: 'Custom Period',
            type: 'custom'
        });

        // CSV Day options
        options.push({ value: 'separator4', label: '--- CSV Days ---', disabled: true });
        for (let i = 1; i <= 7; i++) {
            options.push({
                value: `csv-day-${i}`,
                label: `Day ${i}`,
                csvDay: i,
                type: 'csv-day'
            });
        }

        return options;
    };

    const periodOptions = generatePeriodOptions();

    const handlePeriodSelect = (option) => {
        if (option.disabled) return;

        setSelectedOption(option.value);

        if (option.value !== 'custom') {
            if (option.type === 'csv-day') {
                onPeriodChange({
                    csvDay: option.csvDay,
                    type: 'csv-day'
                });
            } else {
                onPeriodChange({
                    start: option.startDate,
                    end: option.endDate,
                    type: option.type
                });
            }
            setIsDropdownOpen(false);
        }
    };

    const handleCustomPeriodApply = () => {
        if (customStartDate && customEndDate) {
            const startDate = new Date(customStartDate);
            const endDate = new Date(customEndDate);

            onPeriodChange({
                start: startDate,
                end: endDate,
                type: 'custom'
            });
            setIsDropdownOpen(false);
        }
    };

    const getDisplayLabel = () => {
        if (!selectedPeriod) return 'Select Day';

        const option = periodOptions.find(opt => opt.value === selectedOption);
        if (option) {
            return option.label;
        }

        return 'Select Day';
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-1 bg-white dark:bg-gray-800 px-3 py-2 rounded text-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
                <Calendar size={14} />
                <span>{getDisplayLabel()}</span>
                <ChevronDown
                    size={16}
                    className={`transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <div className="absolute right-0 mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                    <div className="py-1">
                        {periodOptions.map((option) => (
                            <div key={option.value}>
                                <button
                                    onClick={() => handlePeriodSelect(option)}
                                    disabled={option.disabled}
                                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${option.disabled
                                        ? 'text-gray-400 cursor-not-allowed font-medium'
                                        : selectedOption === option.value
                                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    {option.label}
                                </button>
                                
                                {/* Custom Date Picker */}
                                {option.value === 'custom' && selectedOption === 'custom' && (
                                    <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                                        <div className="space-y-2">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                    Start Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={customStartDate}
                                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                    End Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={customEndDate}
                                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                                />
                                            </div>
                                            <button
                                                onClick={handleCustomPeriodApply}
                                                disabled={!customStartDate || !customEndDate}
                                                className="w-full px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Custom tooltip for line chart (demand only)
const LineChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{`Time: ${label}`}</p>
                <p className="text-sm" style={{ color: payload[0].color }}>
                    {`Demand: ${payload[0].value.toFixed(3)} kW`}
                </p>
            </div>
        );
    }
    return null;
};

const NoPvHistoricalTab = ({ view, setView, mockEnergyData, COLORS }) => {
    // State for managing selected periods for each chart - updated to support both date periods and CSV days
    const [dailyProfilePeriod, setDailyProfilePeriod] = useState({
        start: new Date(),
        end: new Date(),
        type: 'day'
    });
    const [householdSourcesPeriod, setHouseholdSourcesPeriod] = useState({
        start: new Date(),
        end: new Date(),
        type: 'day'
    });
    const [communitySourcesPeriod, setCommunitySourcesPeriod] = useState({
        start: new Date(),
        end: new Date(),
        type: 'day'
    });

    // Fixed No PV Chart Component - CSV data state
    const [csvData, setCsvData] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [csvError, setCsvError] = useState(null);

    // Load CSV data on component mount
    useEffect(() => {
        loadCSVData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update chart data when selected period changes
    useEffect(() => {
        if (csvData.length > 0) {
            updateChartData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dailyProfilePeriod, csvData]);

    // FIXED: Added proper async/await and parameters
    const loadCSVData = async () => {
        try {
            setLoading(true);
            setCsvError(null);

            console.log("🔄 Loading CSV data for No PV tab...");

            // Use DATA type with proper async call
            const csvResult = await getParsedCsvData('DATA');

            console.log("📊 No PV CSV Results:");
            console.log("- Total rows:", csvResult.totalRows);
            console.log("- Headers:", csvResult.headers);
            console.log("- Sample data:", csvResult.data.slice(0, 3));

            //Use correct parameters for getAvailableDays
            const availableDays = await getAvailableDays('DATA', csvResult.data);
            console.log("- Available days:", availableDays);

            if (csvResult.totalRows === 0) {
                throw new Error("No valid data found in CSV module");
            }

            setCsvData(csvResult.data);
            setLoading(false);

            console.log("✅ CSV data loaded successfully for No PV tab!");

        } catch (err) {
            console.error('❌ Error loading CSV for No PV tab:', err);
            setCsvError(`Failed to load CSV data: ${err.message}`);
            setLoading(false);
            generateFallbackChartData();
        }
    };

    // Added proper async/await and parameters - updated for csvDay format
    const updateChartData = async () => {
        if (csvData.length === 0) {
            console.log("❌ No CSV data available for No PV chart update");
            return;
        }

        try {
            console.log("🔄 Updating No PV chart data for period:", dailyProfilePeriod);

            const { start, type, csvDay } = dailyProfilePeriod;

            if (type === 'csv-day' && csvDay) {
                console.log("📊 No PV - Selected CSV day:", csvDay);
                
                const dayData = await getDataForDay(csvDay, 'DATA', csvData);

                const transformedData = dayData.map(row => ({
                    time: row.time,
                    timeDecimal: row.timeDecimal,
                    demand: row.demand, // Only demand, no generation
                    consumption: row.consumption || row.demand // Fallback to demand if consumption not available
                }));

                console.log("📈 No PV transformed data:", {
                    totalPoints: transformedData.length,
                    sampleData: transformedData.slice(0, 3)
                });

                if (transformedData.length === 0) {
                    console.warn("⚠️ No data found for selected day, using fallback");
                    generateFallbackChartData();
                    return;
                }

                setChartData(transformedData);
            } else if (type === 'day') {
                // Map calendar date to CSV day based on day of week
                const dayOfWeek = start.getDay(); // 0 = Sunday, 6 = Saturday
                const csvDay = (dayOfWeek % 7) + 1; // Map to CSV days 1-7
                
                console.log("📊 No PV - Calendar date mapped to CSV day:", csvDay);
                
                const dayData = await getDataForDay(csvDay, 'DATA', csvData);

                const transformedData = dayData.map(row => ({
                    time: row.time,
                    timeDecimal: row.timeDecimal,
                    demand: row.demand, // Only demand, no generation
                    consumption: row.consumption || row.demand // Fallback to demand if consumption not available
                }));

                console.log("📈 No PV transformed data:", {
                    totalPoints: transformedData.length,
                    sampleData: transformedData.slice(0, 3)
                });

                if (transformedData.length === 0) {
                    console.warn("⚠️ No data found for selected day, using fallback");
                    generateFallbackChartData();
                    return;
                }

                setChartData(transformedData);
            } else {
                // Handle multi-day periods (weeks/months/custom) - generate sample data
                console.log("📊 No PV - Generating sample data for period type:", type);
                const sampleData = generateSampleDataForPeriod(type, 'household');
                setChartData(sampleData);
            }
        } catch (error) {
            console.error("❌ Error updating No PV chart data:", error);
            setCsvError(`Error updating No PV chart: ${error.message}`);
            generateFallbackChartData();
        }
    };

    const generateFallbackChartData = () => {
        console.log("🔄 Generating No PV fallback data...");

        const fallbackData = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let mins = 0; mins < 60; mins += 15) {
                const timeString = `${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;

                // More realistic demand curve for household without PV
                let demand = 0.8; // Base load
                if (hour >= 7 && hour <= 9) demand += 1.5; // Morning peak
                if (hour >= 17 && hour <= 21) demand += 2.0; // Evening peak
                demand += Math.random() * 0.4; // Some variation

                fallbackData.push({
                    time: timeString,
                    timeDecimal: hour + mins / 60,
                    demand: parseFloat(demand.toFixed(3)),
                    consumption: parseFloat(demand.toFixed(3))
                });
            }
        }

        console.log("Generated No PV fallback data points:", fallbackData.length);
        setChartData(fallbackData);
    };

    // Helper function to generate sample data for different time periods
    const generateSampleDataForPeriod = (periodType, dataType = 'household') => {
        const data = [];
        
        if (periodType === 'week') {
            // Generate 7 days of data
            for (let day = 0; day < 7; day++) {
                for (let hour = 0; hour < 24; hour++) {
                    for (let mins = 0; mins < 60; mins += 15) {
                        const timeString = `Day ${day + 1} ${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                        
                        let demand = 0.8; // Base load
                        if (hour >= 7 && hour <= 9) demand += 1.5; // Morning peak
                        if (hour >= 17 && hour <= 21) demand += 2.0; // Evening peak
                        demand += Math.random() * 0.4; // Some variation
                        
                        data.push({ 
                            time: timeString, 
                            timeDecimal: hour + mins / 60,
                            demand: parseFloat(demand.toFixed(3)),
                            consumption: parseFloat(demand.toFixed(3))
                        });
                    }
                }
            }
        } else if (periodType === 'month') {
            // Generate 30 days of data (simplified - one point per day)
            for (let day = 0; day < 30; day++) {
                const timeString = `Day ${day + 1}`;
                
                let demand = 0.8 + Math.random() * 0.5;
                data.push({ 
                    time: timeString, 
                    timeDecimal: day,
                    demand: parseFloat(demand.toFixed(3)),
                    consumption: parseFloat(demand.toFixed(3))
                });
            }
        } else if (periodType === 'custom') {
            // Generate data based on custom date range
            const daysDiff = Math.ceil((new Date() - new Date()) / (1000 * 60 * 60 * 24)) + 1;
            const daysToGenerate = Math.min(daysDiff, 30); // Limit to 30 days max
            
            for (let day = 0; day < daysToGenerate; day++) {
                const timeString = `Day ${day + 1}`;
                
                let demand = 0.8 + Math.random() * 0.5;
                data.push({ 
                    time: timeString, 
                    timeDecimal: day,
                    demand: parseFloat(demand.toFixed(3)),
                    consumption: parseFloat(demand.toFixed(3))
                });
            }
        } else {
            // Default: single day data
            for (let hour = 0; hour < 24; hour++) {
                for (let mins = 0; mins < 60; mins += 15) {
                    const timeString = `${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                    
                    let demand = 0.8; // Base load
                    if (hour >= 7 && hour <= 9) demand += 1.5; // Morning peak
                    if (hour >= 17 && hour <= 21) demand += 2.0; // Evening peak
                    demand += Math.random() * 0.4; // Some variation
                    
                    data.push({ 
                        time: timeString, 
                        timeDecimal: hour + mins / 60,
                        demand: parseFloat(demand.toFixed(3)),
                        consumption: parseFloat(demand.toFixed(3))
                    });
                }
            }
        }
        
        return data;
    };

    // Optional: Add debug logging specifically for No PV
    const debugNoPVData = () => {
        console.log("🔍 Debugging No PV CSV data:");
        console.log("- CSV Data length:", csvData.length);
        console.log("- Chart Data length:", chartData.length);
        console.log("- Loading state:", loading);
        console.log("- Error state:", csvError);

        if (csvData.length > 0) {
            console.log("- Sample CSV row:", csvData[0]);
            console.log("- CSV columns:", Object.keys(csvData[0]));
        }

        if (chartData.length > 0) {
            console.log("- Sample chart data:", chartData[0]);
            console.log("- Chart data structure:", Object.keys(chartData[0]));
        }
    };

    // Add this useEffect for debugging if needed
    useEffect(() => {
        if (!loading) {
            debugNoPVData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, csvData, chartData]);

    // Mock data for peak demand
    const peakDemandData = [
        { day: 'Mon', value: 2.2, time: '08h30' },
        { day: 'Tues', value: 3.0, time: '08h30' },
        { day: 'Wed', value: 3.2, time: '09h00' },
        { day: 'Thur', value: 2.5, time: '09h00' },
        { day: 'Fri', value: 1.8, time: '08h30' },
        { day: 'Sat', value: 1.0, time: '17h30' },
        { day: 'Sun', value: 4.0, time: '13h30' }
    ];

    // Custom tooltip for peak demand
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-2 border border-gray-200 rounded shadow-md">
                    <p className="text-sm">Time of day when peak demand occurred: <strong>{payload[0].payload.time}</strong></p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className='w-full'>
            {/* Container for the view buttons - positioned at top right */}
            <div className="flex justify-end mb-4">
                <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">View:</span>
                    <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-full p-1">
                        <button
                            className={`text-xs px-2 py-1 rounded-full ${view === 'household' ? 'bg-blue-500 text-white' : 'text-gray-500 dark:text-gray-400'}`}
                            onClick={() => setView('household')}
                        >
                            Household
                        </button>
                        <button
                            className={`text-xs px-2 py-1 rounded-full ${view === 'community' ? 'bg-blue-500 text-white' : 'text-gray-500 dark:text-gray-400'}`}
                            onClick={() => setView('community')}
                        >
                            Community
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Daily Profile (Household) - Now with real CSV data but DEMAND ONLY */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">Daily Profile (Household)</h3>
                        <TimelineDropdown
                            selectedPeriod={dailyProfilePeriod}
                            onPeriodChange={setDailyProfilePeriod}
                        />
                    </div>

                    {loading ? (
                        <div className="h-72 flex items-center justify-center">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">Loading data...</p>
                            </div>
                        </div>
                    ) : csvError ? (
                        <div className="h-72 flex items-center justify-center">
                            <div className="text-center">
                                <p className="text-red-600 dark:text-red-400 text-sm mb-1">{csvError}</p>
                                <p className="text-gray-600 dark:text-gray-400 text-xs">Using sample data</p>
                            </div>
                        </div>
                    ) : (
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis
                                        dataKey="time"
                                        tick={{ fontSize: 12 }}
                                        interval="preserveStartEnd"
                                        tickFormatter={(value, index) => {
                                            // For multi-day data (less than 40 points), show more ticks
                                            if (chartData.length <= 40) {
                                                // Show every 2-3 ticks for weeks/months
                                                const interval = chartData.length <= 10 ? 1 : Math.ceil(chartData.length / 8);
                                                if (index % interval === 0) return value;
                                            } else {
                                                // For single day with many points, show fewer ticks
                                                if (index % 16 === 0) return value;
                                            }
                                            return '';
                                        }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Power (kW)', angle: -90, position: 'insideLeft' }}
                                    />
                                    <Tooltip content={<LineChartTooltip />} />
                                    {/* Only demand line - no generation for No PV */}
                                    <Line
                                        type="monotone"
                                        dataKey="demand"
                                        stroke="#FF8042"
                                        strokeWidth={3}
                                        name="Demand"
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>

                            {/* Legend - only demand */}
                            <div className="flex justify-center mt-2">
                                <div className="flex items-center">
                                    <div className="w-4 h-4 bg-orange-500 rounded-full mr-2"></div>
                                    <span className="text-xs text-gray-700 dark:text-gray-300">Demand</span>
                                </div>
                            </div>

                            {/* Data Info */}
                            <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Data for Day {dailyProfilePeriod.csvDay} • {chartData.length} data points (15-min intervals)
                            </div>
                        </div>
                    )}
                </div>

                {/* Energy Sources (Household) */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className='flex justify-between items-center mb-4'>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">Energy Sources (Household)</h3>
                        <TimelineDropdown
                            selectedPeriod={householdSourcesPeriod}
                            onPeriodChange={setHouseholdSourcesPeriod}
                        />
                    </div>

                    <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                            <Pie
                                data={mockEnergyData.historicalNoPvHouseholdSources}
                                cx="50%"
                                cy="50%"
                                outerRadius={70}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                                {mockEnergyData.historicalNoPvHouseholdSources.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 flex justify-center">
                        <div className="grid grid-cols-2 gap-2">
                            <div className="flex items-center">
                                <Sun size={16} className="text-yellow-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Community</span>
                            </div>
                            <div className="flex items-center">
                                <Home size={16} className="text-green-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Utility</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Daily Peak Demand */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <h3 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Daily Peak Demand</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={peakDemandData} margin={{ top: 30, right: 30, left: 20, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="day" />
                                <YAxis
                                    label={{ value: 'Peak load of your dwelling (kW)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                                    domain={[0, 5]}
                                    ticks={[0, 1, 2, 3, 4, 5]}
                                />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }} />
                                <Bar
                                    dataKey="value"
                                    fill="#FFECD1"
                                    stroke="#FFC107"
                                    strokeWidth={1}
                                    isAnimationActive={false}
                                >
                                    {peakDemandData.map((entry, index) => (
                                        <text
                                            x={0}
                                            y={0}
                                            dx={30}
                                            dy={-10}
                                            textAnchor="middle"
                                            fill="#1A202C"
                                            fontSize={12}
                                            key={`time-${index}`}
                                        >
                                            {entry.time}
                                        </text>
                                    ))}
                                    <LabelList dataKey="value" position="center" formatter={(value) => `${value.toFixed(1)}kW`} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-4 text-center">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Day of the week</p>
                    </div>
                </div>

                {/* Energy Sources (Community) */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">Energy Sources (Community)</h3>
                        <TimelineDropdown
                            selectedPeriod={communitySourcesPeriod}
                            onPeriodChange={setCommunitySourcesPeriod}
                        />
                    </div>

                    <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                            <Pie
                                data={mockEnergyData.historicalCommunitySources}
                                cx="50%"
                                cy="50%"
                                outerRadius={70}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                                {mockEnergyData.historicalCommunitySources.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 flex justify-center">
                        <div className="grid grid-cols-3 gap-2">
                            <div className="flex items-center">
                                <Sun size={16} className="text-yellow-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Solar</span>
                            </div>
                            <div className="flex items-center">
                                <Wind size={16} className="text-blue-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Wind</span>
                            </div>
                            <div className="flex items-center">
                                <Home size={16} className="text-green-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Utility</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* How much of your demand is met by community RE? */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow md:col-span-2">
                    <div className='flex mb-2'>
                        <h3 className="font-medium mb-2 text-gray-900 dark:text-gray-100">How much of your demand is met by community RE</h3>
                    </div>
                    <div className="flex items-center justify-center w-full">
                        <div className="w-24 h-24 rounded-full border-4 border-blue-500 flex items-center justify-center">
                            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">39%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NoPvHistoricalTab;